import { action, observable, computed, autorun } from 'mobx';
import Alert from 'react-s-alert';
import i18next from 'i18next';

import { AppManager, Variables, Input } from 'core';
import { unObserve } from 'core/utils';

import { FormStore } from 'libs/forms/app';

import { countriesValues } from 'apps/payment/countries';
import { modalStore } from 'apps/modals/app';
import { clientStore } from 'apps/client/app';
import AddressModal from 'apps/addresses/blocks/AddressModal';

import { AddressModel } from './models';
import {
  AddressesQuery,
  CreateAddressMutation,
  UpdateAddressMutation,
  DeleteAddressMutation,
} from './gql';

// TO ADD:
// company: {
//   label: 'Company',
//   optional: true,
// },

const addressConfig = {
  addAddressForm: {
    fields: [
      'name',
      'company',
      'address1',
      'address2',
      'city',
      'province',
      'zip',
      'country',
      'phone',
      'default',
    ],
    styles: {
      formColumns: 2,
      labelBold: true,
    },
    buttonText: i18next.t('Add Address'),
    buttonDataTestId: 'addAddressForm-add-address-button',
    buttonTextPosting: i18next.t('Adding Address...'),
  },
  title: i18next.t('Add Address'),
  fields: {
    name: {
      label: i18next.t('Name'),
    },
    company: {
      label: i18next.t('Company'),
      optional: true,
    },
    address1: {
      label: i18next.t('Address 1'),
      placeholder: i18next.t('Street Address'),
    },
    address2: {
      label: i18next.t('Address 2'),
      placeholder: i18next.t('Apt, suite, unit, building, floor, etc.'),
      optional: true,
    },
    city: {
      label: i18next.t('City'),
    },
    province: {
      label: i18next.t('State/Province'),
      optional: true,
    },
    zip: {
      label: i18next.t('Zip or Postcode'),
    },
    country: {
      /* Todo: make and use global 'Country' field config.
      -- Requires turning libs/forms/fields.yaml to js so we can include js variables, as we should with all configs! */
      appKey: 'value',
      labelKey: 'label',
      label: i18next.t('Country'),
      component: 'forms.SelectField',
      selectionComponent: 'payment.countrySelection',
      placeholder: i18next.t('Please select'),
      options: {
        optionComponent: 'payment.countryOption',
        values: countriesValues,
      },
    },
    phone: {
      label: i18next.t('Phone Number'),
      fieldStore: 'forms.PhoneFieldStore',
      component: 'forms.PhoneField',
      placeholder: i18next.t('Intl. format e.g. +34 662 21 3038'),
      optional: true,
    },
    default: {
      label: i18next.t('Make Default Address'),
      fieldStore: 'forms.BoolFieldStore',
      component: 'forms.CheckboxField',
      optional: true,
    },
  },
};

export class AddressesAppManager extends AppManager {
  static registryName = 'AddressesAppManager';
  @observable addingAddress = false;
  @observable changingAddress = false;
  @observable addresses = [];
  @observable loadAllAddresses = false;
  @observable.ref addressMutation = null;
  @observable deleting = null;

  selectAddressIdWhenAvailable = null;

  @computed get loadDefaultOnly() {
    // return !this.changingAddress && !this.loadAllAddresses;
    return false;
  }

  constructor(parent, { fieldStore }) {
    super(parent);
    this.fieldStore = fieldStore;
    // make addresses request
    // remake addresses request on adding a new address

    const { preventAutoSelectionOfDefaultAddress, addressSpaceId } =
      fieldStore.conf;

    if (!preventAutoSelectionOfDefaultAddress) {
      this.disposers.push(
        autorun(() => {
          // set the selected field to default address if it is empty
          if (this.defaultAddress && !this.fieldStore.value) {
            this.fieldStore.UpdateValue(this.defaultAddress);
          }
        }),
      );
    }
    this.variables = new Variables({
      input: () => {
        // should use structural comparison of computed's to ensure that it doesn't fire
        // an update when nothing changed. Instead we encapsulate the accessess in 'loadDefaultOnly'
        return {
          spaceId: addressSpaceId || clientStore.activeSpaceId,
          default: this.loadDefaultOnly, // just load the default address if true
        };
      },
    });
    this.variables.onChange(
      ['input.default', 'input.spaceId'],
      this.QueryAddresses,
    );

    this.updateAddressMutation = null;
  }

  @action QueryAddresses = () => {
    if (!this.variables.input.spaceId) return;

    this.addressesQuery = new AddressesQuery(this, {
      queryKey: 'addresses',
      onSysErr: this.onAddressQueryError,
      variables: this.variables,
    });
    this.addressesQuery.process(
      addresses => addresses?.map(addr => AddressModel.Load(addr)) || [],
      this.UpdateAddresses,
    );
    this.addressesQuery.query();
  };

  @action onAddressQueryError = () => {
    Alert.error(i18next.t('Unable to get addresses, please try again'));
  };

  @action UpdateAddresses = addresses => {
    if (this.addressesQuery.loading) {
      // Why do we call UpdateAddresses when the query is still loading?
      return;
    }
    this.addresses = addresses;

    // select this address when we get the data, this happens when we just added a new address
    if (this.selectAddressIdWhenAvailable) {
      const addressToSelect = this.addresses.find(
        address => address.id === this.selectAddressIdWhenAvailable,
      );
      if (addressToSelect) {
        this.ShipTo(addressToSelect);
        this.selectAddressIdWhenAvailable = null;
      }
    }

    // if the current selected address is not in the addresses (was deleted) - remove the selection.
    if (!this.fieldStore.conf.doNotDeselectOnDeletion) {
      const selectedId = this.fieldStore.value?.id;
      if (selectedId) {
        const selectedAddr = this.addresses.find(
          address => address.id === selectedId,
        );
        if (selectedAddr === undefined) {
          this.fieldStore.UpdateValue(null);
        }
      }
    }
  };

  @action BeginChangeAddress = (description = '') => {
    this.changingAddress = true;
    if (this.addresses.length === 0) {
      this.OpenAddAddress();
    }
    // if we change the address from the default one we should always keep all addresses loaded
    // in the meantime the user may have selected a non-default address which must remain in the list to remain selected
    this.loadAllAddresses = true;
    modalStore.SetModalComponent(
      AddressModal,
      {
        manager: this,
        toFront: true,
        size: 'large',
        description,
      },
      this.OnCloseModal,
    );
  };

  @action OnCloseModal = () => {
    this.changingAddress = false;
    this.addingAddress = false;
  };

  @action OpenAddAddress = () => {
    this.addingAddress = true;
    this.newAddress = new AddressModel();
    // if we adding the first address - don't give the default option
    let finalAddresConf;
    if (this.addresses.length === 0) {
      finalAddresConf = JSON.parse(JSON.stringify(addressConfig));
      finalAddresConf.addAddressForm.fields.pop(); // remove 'default' field
    } else {
      finalAddresConf = addressConfig;
    }
    this.addAddressFormStore = new FormStore(
      this.newAddress,
      finalAddresConf,
      'addAddressForm',
      false,
    );
  };

  @action AddAddress = () => {
    function remove_plus(phoneVal) {
      if (phoneVal === '+') {
        return '';
      }
      return phoneVal;
    }
    if (this.addAddressFormStore.isValid) {
      const { newAddress } = this;
      const createAddressInput = new Input({
        input: {
          name: newAddress.name,
          company: newAddress.company.trim(),
          address1: newAddress.address1,
          address2: newAddress.address2,
          city: newAddress.city,
          province: newAddress.province,
          country: newAddress.country,
          zip: newAddress.zip,
          phone: remove_plus(this.newAddress.phone),
          default: newAddress.default || this.addresses.length === 0,
          spaceId:
            this.fieldStore.conf.addressSpaceId || clientStore.activeSpaceId,
        },
      });

      this.addressMutation = new CreateAddressMutation(
        createAddressInput,
        null,
        {
          onSysErr: this.AddAddressErr,
          onUserErr: this.AddAddressValidationErr,
          onSuccess: this.AddressAdded,
        },
        true,
      );

      this.addressMutation.mutate();
      this.addAddressFormStore.SetPosting(true);
    } else {
      this.addAddressFormStore.alert();
    }
  };

  @action AddAddressValidationErr = errs => {
    this.addAddressFormStore.SetServerErrs(errs);
    this.addAddressFormStore.SetPosting(false);
  };

  @action AddAddressErr = () => {
    Alert.error(
      i18next.t(
        "Sorry due to a fault we couldn't add that address. Please try again...",
      ),
    );
    this.addAddressFormStore.SetPosting(false);
  };

  @action AddressAdded = addedAddress => {
    this.selectAddressIdWhenAvailable = addedAddress.id;
    this.addingAddress = false;
    Alert.success(i18next.t('Address Added.'));
    // if we just added the first address close the Modal
    if (this.addresses.length === 0) {
      modalStore.Close();
    }
    this.addressesQuery.refetchQuery();
    this.addAddressFormStore.SetPosting(false);
  };

  @action MakeDefault = address => {
    // call UpdateAddress mutation
    if (this.updateAddressMutation?.status === 0 || address.default === true) {
      return;
    }
    const updateAddressInput = new Input({
      input: {
        default: true,
        addressId: address.id,
      },
    });
    this.updateAddressMutation = new UpdateAddressMutation(
      updateAddressInput,
      null,
      {
        onSysErr: this.MakeDefaultError,
        onSuccess: this.updateDefaultAddressInCache,
      },
      true,
    );
    // eslint-disable-next-line no-param-reassign
    address.loading = true;
    this.updateAddressMutation.process(
      addr => AddressModel.Load(addr),
      this.UpdateDefault,
    );
    this.updateAddressMutation.mutate();
  };

  @action MakeDefaultError = () => {
    this.ResetAddressLoading();
    Alert.error(
      i18next.t(
        "Sorry due to a fault we couldn't set the address. Please check your connection and try again...",
      ),
    );
  };

  @action UpdateDefault = updatedAddress => {
    // eslint-disable-next-line no-param-reassign
    updatedAddress.loading = false;
  };

  updateDefaultAddressInCache = (updatedAddress, apolloCache) => {
    /*
    We just setup an address as the new default. We need to mark the other addresses as not default,
    because presumably there was a pre-existing different default and we would end up with both as defaults.

    We can't simply override the local data because this UpdateAddressMutation() will
    trigger the "process" function for the AddressesQuery, which will feed data from the ApolloCache
    and override any changes we made here, because the ApolloCache only got updated for the mutated address
    but not the previous default.

    The simple solution is to just refetch the query with
      this.addressesQuery.refetchQuery();
    it used to be the case that this refetching happend automatically but that is no longer the case. Unclear why.

    We can also, however, just update the local cache manually, this is what the following code does.
    This way we prevent an extra query, but more than anything serves as sample approach that may be useful
    in the future.
    */

    // simple solution just call this and do nothing else
    // this.addressesQuery.refetchQuery();

    if (!updatedAddress.default) return;

    const query = AddressesQuery.conf.GQL;
    const variables = unObserve(this.variables);

    const addresses = apolloCache.readQuery({ query, variables })?.addresses;

    if (!addresses) {
      // fallback, should not happen by cache may be evicted
      this.addressesQuery.refetchQuery();
      return;
    }

    const addressesToCache = addresses.map(addr => {
      if (addr.id === updatedAddress.id)
        return {
          ...addr,
          default: updatedAddress.default,
        };
      return {
        ...addr,
        default: false,
      };
    });

    apolloCache.writeQuery({
      query,
      variables,
      data: { addresses: addressesToCache },
    });
  };

  @computed get defaultAddress() {
    // eslint-disable-next-line no-restricted-syntax
    for (const addr of this.addresses) {
      if (addr.default) {
        return addr;
      }
    }
    return null;
  }

  @action ShipTo = address => {
    this.fieldStore.UpdateValue(address);
    this.changingAddress = false;
    modalStore.Close();
  };

  @action StartDelete = address => {
    this.deleting = address.id;
  };

  @action CancelDelete = () => {
    this.deleting = null;
  };

  @action Delete = address => {
    if (address.loading) {
      return;
    }
    const deleteAddressMutation = new DeleteAddressMutation(
      new Input({
        input: {
          addressId: address.id,
        },
      }),
      null,
      {
        onSysErr: this.DeleteAddressErr,
        // onUserErr: this.onRfqCreationErr,
        onSuccess: this.AddressDeleted,
      },
      true,
    );
    // eslint-disable-next-line no-param-reassign
    address.loading = true;
    deleteAddressMutation.mutate();
  };

  @action ResetAddressLoading = () => {
    this.addresses.forEach(address => {
      // eslint-disable-next-line no-param-reassign
      address.loading = false;
    });
  };

  @action DeleteAddressErr = () => {
    /* Note that onSysErr does not seem to be catching ApolloError */
    Alert.error(
      i18next.t(
        "Sorry due to a fault we couldn't add that address. Please check your internet connection and try again...",
      ),
    );
    this.ResetAddressLoading();
  };

  @action AddressDeleted = () => {
    Alert.success(i18next.t('Address Deleted.'));
    // refetch the query with new elements
    this.addressesQuery.refetchQuery();
  };
}
